"use client"

import { useState } from "react"
import Link from "next/link"
import { useSmartBack } from "@/lib/use-smart-back"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Textarea } from "@/components/ui/textarea"
import { Label } from "@/components/ui/label"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { ArrowLeft, CheckCircle2, AlertCircle, Send } from "@/lib/icons"
import { toast } from "sonner"
import { useTranslations } from "next-intl"

const CATEGORIES = [
  { value: "bug", label: "Bug / Glitch" },
  { value: "payment", label: "Payment Issue" },
  { value: "account", label: "Account Problem" },
  { value: "dmca", label: "DMCA / Copyright" },
  { value: "commission", label: "Commission Dispute" },
  { value: "moderation", label: "Moderation / Content Report" },
  { value: "feature", label: "Feature Request" },
  { value: "other", label: "Other" },
]

const PRIORITIES = [
  { value: "low", label: "Low — not urgent" },
  { value: "medium", label: "Medium — affecting my experience" },
  { value: "high", label: "High — can't use the platform" },
]

export default function SupportPage() {
  const t = useTranslations("supportPage")
  const goBack = useSmartBack()
  const [title, setTitle] = useState("")
  const [description, setDescription] = useState("")
  const [category, setCategory] = useState("")
  const [priority, setPriority] = useState("medium")
  const [email, setEmail] = useState("")
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [submitted, setSubmitted] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError(null)

    if (!title.trim() || !description.trim() || !category) {
      setError(t("pleaseFillInAllRequiredFields"))
      return
    }

    setIsSubmitting(true)

    try {
      const res = await fetch("/api/support/tickets", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          title: title.trim(),
          description: description.trim(),
          category,
          priority,
          contact_email: email.trim() || null,
          url: window.location.href,
          browser: navigator.userAgent,
        }),
      })

      if (!res.ok) {
        const data = await res.json().catch(() => ({}))
        throw new Error(data.error || "Failed to submit ticket")
      }

      setSubmitted(true)
      toast.success(t("ticketSubmittedSuccessfully"))
    } catch (err: any) {
      setError(err.message || "Something went wrong. Please try again.")
    } finally {
      setIsSubmitting(false)
    }
  }

  if (submitted) {
    return (
      <main className="min-h-screen bg-background flex items-center justify-center p-4">
        <div className="max-w-md w-full text-center space-y-4">
          <CheckCircle2 className="h-16 w-16 text-green-500 mx-auto" />
          <h1 className="text-2xl font-bold">{t("ticketSubmitted")}</h1>
          <p className="text-muted-foreground">
            {t("weVeReceivedYourSupportRequestAndWillGet")}
          </p>
          <div className="flex gap-3 justify-center pt-4">
            <Button variant="outline" asChild>
              <Link href="/">{t("backToHome")}</Link>
            </Button>
            <Button onClick={() => { setSubmitted(false); setTitle(""); setDescription(""); setCategory(""); }}>
              {t("submitAnother")}
            </Button>
          </div>
        </div>
      </main>
    )
  }

  return (
    <main className="min-h-screen bg-background">
      <div className="max-w-2xl mx-auto py-8 px-4 md:px-8">
        <div className="mb-8">
          <Button variant="ghost" size="sm" className="gap-2 mb-4" onClick={goBack}>
            <ArrowLeft className="h-4 w-4" />
            {t("backToHome")}
          </Button>
          <h1 className="text-3xl font-bold mb-2">{t("support")}</h1>
          <p className="text-muted-foreground">
            {t("havingAnIssueSubmitATicketAndWeLlLook")}
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          {error && (
            <div className="flex items-start gap-3 p-4 rounded-lg border border-red-500/30 bg-red-500/5">
              <AlertCircle className="h-5 w-5 text-red-500 flex-shrink-0 mt-0.5" />
              <p className="text-sm text-red-400">{error}</p>
            </div>
          )}

          {/* Category */}
          <div className="space-y-2">
            <Label htmlFor="category">{t("category")}</Label>
            <Select value={category} onValueChange={setCategory}>
              <SelectTrigger>
                <SelectValue placeholder={t("whatSThisAbout")} />
              </SelectTrigger>
              <SelectContent>
                {CATEGORIES.map((cat) => (
                  <SelectItem key={cat.value} value={cat.value}>
                    {cat.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Title */}
          <div className="space-y-2">
            <Label htmlFor="title">{t("subject")}</Label>
            <Input
              id="title"
              placeholder={t("briefSummaryOfTheIssue")}
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              maxLength={200}
            />
          </div>

          {/* Description */}
          <div className="space-y-2">
            <Label htmlFor="description">{t("description")}</Label>
            <Textarea
              id="description"
              placeholder={t("describeWhatHappenedWhatYouExpectedAndA")}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={6}
              className="resize-none"
            />
          </div>

          {/* Priority */}
          <div className="space-y-2">
            <Label htmlFor="priority">{t("priority")}</Label>
            <Select value={priority} onValueChange={setPriority}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {PRIORITIES.map((p) => (
                  <SelectItem key={p.value} value={p.value}>
                    {p.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Contact email (optional for guests) */}
          <div className="space-y-2">
            <Label htmlFor="email">{t("contactEmailOptional")}</Label>
            <Input
              id="email"
              type="email"
              placeholder={t("yourEmailComSoWeCanFollowUp")}
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
            <p className="text-xs text-muted-foreground">
              {t("ifYouReLoggedInWeLlUseYourAccountEmai")}
            </p>
          </div>

          {/* Submit */}
          <Button type="submit" className="w-full" disabled={isSubmitting} size="lg">
            {isSubmitting ? (
              <>{t("submitting")}</>
            ) : (
              <>
                <Send className="h-4 w-4 mr-2" />
                {t("submitTicket")}
              </>
            )}
          </Button>

          <p className="text-xs text-muted-foreground text-center">
            {t.rich("forDmcaIssuesYouCanAlsoEmail", { link: (chunks) => <a href="mailto:dmca@goonru.app" className="text-accent hover:underline">{chunks}</a> })}
          </p>
        </form>
      </div>
    </main>
  )
}
